import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  EndpointRelationship,
  MoveRelationshipRequest,
  UpdateRelationshipRequest,
} from '../../types/case-container'

export interface EditLinkFormState {
  formData: UpdateRelationshipRequest
  setFormData: (data: UpdateRelationshipRequest) => void
  showChangeEndpoints: boolean
  setShowChangeEndpoints: (show: boolean) => void
  newSourceId: string
  setNewSourceId: (id: string) => void
  newTargetId: string
  setNewTargetId: (id: string) => void
  moveError: string | null
  isOpenQuestion: boolean
  hasEndpointChanges: boolean
  hasSelfLoop: boolean
  handleSubmit: (e: React.FormEvent) => void
  handleStatusChange: (status: string) => void
  handleMoveEndpoints: () => Promise<void>
}

interface UseEditLinkFormArgs {
  relationship: EndpointRelationship
  onClose: () => void
  onUpdate: (data: UpdateRelationshipRequest) => void
  onMove?: (request: MoveRelationshipRequest) => Promise<void>
}

function buildInitialFormData(relationship: EndpointRelationship): UpdateRelationshipRequest {
  return {
    connectionType: relationship.connectionType,
    status: relationship.status,
    label: relationship.label ?? '',
    investigationNotes: relationship.investigationNotes ?? '',
  }
}

function buildMoveRequest(
  relationship: EndpointRelationship,
  newSourceId: string,
  newTargetId: string
): MoveRelationshipRequest {
  const request: MoveRelationshipRequest = {}
  if (newSourceId !== relationship.sourceEndpointId) {
    request.newSourceEndpointId = newSourceId
  }
  if (newTargetId !== relationship.targetEndpointId) {
    request.newTargetEndpointId = newTargetId
  }
  return request
}

export function useEditLinkForm({
  relationship,
  onClose,
  onUpdate,
  onMove,
}: UseEditLinkFormArgs): EditLinkFormState {
  const { t } = useTranslation('cases')
  const [formData, setFormData] = useState<UpdateRelationshipRequest>(() =>
    buildInitialFormData(relationship)
  )
  const [showChangeEndpoints, setShowChangeEndpoints] = useState(false)
  const [newSourceId, setNewSourceId] = useState<string>(relationship.sourceEndpointId)
  const [newTargetId, setNewTargetId] = useState<string>(relationship.targetEndpointId)
  const [moveError, setMoveError] = useState<string | null>(null)

  const isOpenQuestion = formData.status === 'open-question'
  const hasEndpointChanges =
    newSourceId !== relationship.sourceEndpointId || newTargetId !== relationship.targetEndpointId
  const hasSelfLoop = newSourceId === newTargetId

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    onUpdate(formData)
  }

  const handleStatusChange = (status: string): void => {
    setFormData({ ...formData, status })
  }

  const handleMoveEndpoints = async (): Promise<void> => {
    if (!onMove || !hasEndpointChanges || hasSelfLoop) return
    setMoveError(null)
    const request = buildMoveRequest(relationship, newSourceId, newTargetId)
    try {
      await onMove(request)
      onClose()
    } catch (err) {
      setMoveError(
        err instanceof Error ? err.message : t('infrastructure.editLink.moveFailedGeneric')
      )
    }
  }

  return {
    formData,
    setFormData,
    showChangeEndpoints,
    setShowChangeEndpoints,
    newSourceId,
    setNewSourceId,
    newTargetId,
    setNewTargetId,
    moveError,
    isOpenQuestion,
    hasEndpointChanges,
    hasSelfLoop,
    handleSubmit,
    handleStatusChange,
    handleMoveEndpoints,
  }
}
